/**
 * StyleGradeTab — V2 Style Grade options view for the AI Workspace.
 * Shows dropdowns (Enhance → Model → Scope), checkboxes, collapsible prompt,
 * generate button, and a ShotGrid below.
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import type { StylePreset, AiWorkspaceScope } from '@/hooks/useAiWorkspaceFlow.ts';
import type { GenerationHistoryEntry } from '@/types/generationHistory.ts';
import { ShotGrid } from './ShotGrid.tsx';
import { WorkspaceShotPreview } from './WorkspaceShotPreview.tsx';
import { ImageFlyOverlay } from '@/components/ImageFlyOverlay.tsx';
import { PromptPopover } from './PromptPopover.tsx';
import { DropdownPicker } from '@/components/DropdownPicker.tsx';
import { SparkleIcon } from '@/components/icons/SparkleIcon.tsx';
import { useProjectStore } from '@/store/projectStore.ts';
import { useCommentStore } from '@/store/commentStore.ts';
import { useAccountStore } from '@/store/accountStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { useTutorialStore, selectExampleTourActive } from '@/store/tutorialStore.ts';
import { canAfford } from '@/ai/tokenCosts.ts';
import { capabilities } from '@/capabilities.ts';
import { runGenerateGate } from './generateGate.ts';
import { formatTimeEstimate } from '@/components/CostBanner.tsx';
import type { AiWorkspaceTask } from '@/store/uiStore.ts';
import type { TimelineShot, ScreenplayAction } from '@/types/timeline.ts';
import type { CatalogModel, ModelCategory, CostEstimate } from '@/types/ai.ts';
import { useMobileLayout } from '@/hooks/useMobileLayout.ts';
import styles from './AiWorkspace.module.css';

const TIER_LABEL_KEYS = ['tierLabelFast', 'tierLabelQuality', 'tierLabelBest'] as const;

const TASK_LABEL_KEYS: Record<AiWorkspaceTask, string> = {
  enhance: 'taskLabelImage',
  video: 'taskLabelVideo',
  continuous: 'taskLabelFirstLastShotVideo',
};

interface Props {
  // Task / Model
  task: AiWorkspaceTask;
  onTaskChange: (task: AiWorkspaceTask) => void;
  continuousAvailable: boolean;
  activeModelId: string;
  catalog: CatalogModel[];
  onSelectModel: (category: 'image' | 'video' | 'tts', modelId: string) => void;
  onOpenHistory: () => void;

  // Scope
  scope: AiWorkspaceScope;
  onScopeChange: (scope: AiWorkspaceScope, sceneId?: number) => void;
  overwriteExisting: boolean;
  onToggleOverwrite: (on: boolean) => void;
  showOverwriteOption: boolean;
  skipApproved: boolean;
  onToggleSkipApproved: (on: boolean) => void;

  // Scope shots
  isEnhance: boolean;
  scopeShots: TimelineShot[];

  // Prompt
  editablePrompt: string;
  onPromptChange: (p: string) => void;
  stylePreset: StylePreset;
  onStylePresetChange: (preset: StylePreset) => void;
  editableMetadata: string;
  onMetadataChange: (m: string) => void;
  onPromptReset: () => void;

  // Video options
  generateAudio: boolean;
  onToggleAudio: (on: boolean) => void;
  retimeToMatchVideo: boolean;
  onToggleRetime: () => void;
  audioHint: string;
  onAudioHintChange: (hint: string) => void;

  // Cost / counts
  costEstimate: CostEstimate;
  estTotalSeconds: number;
  hasClamping: boolean;
  durationLimits: { min: number; max: number } | null;

  // Revert / Restore
  canRevert: boolean;
  canRestore: boolean;
  restoreLabel: string;
  restoreEntries: GenerationHistoryEntry[];
  onRevert: () => void;
  onRestore: (historyId?: string) => void;

  // Actions
  onGenerate: () => void;
  onClose: () => void;
  generateDisabled: boolean;

  // Selection
  selectedShotIds: Set<number>;
  setSelectedShotIds: (ids: Set<number>) => void;
  toggleShotSelection: (shotId: number) => void;
  toggleSceneSelection: (shotId: number) => void;
  onSetInitialPair: (pair: { fromShotId: number; toShotId: number } | null) => void;

  // Auto-scope switching flag
  autoScopeSwitch: boolean;
  setAutoScopeSwitch: (on: boolean) => void;

  // Screenplay
  hasScreenplay: boolean;
  sceneActions: ScreenplayAction[];
}

export function StyleGradeTab({
  task, onTaskChange, continuousAvailable, activeModelId, catalog, onSelectModel, onOpenHistory,
  scope, onScopeChange, overwriteExisting, onToggleOverwrite, showOverwriteOption,
  skipApproved, onToggleSkipApproved,
  isEnhance, scopeShots,
  editablePrompt, onPromptChange,
  stylePreset, onStylePresetChange,
  editableMetadata, onPromptReset,
  generateAudio, onToggleAudio, retimeToMatchVideo, onToggleRetime,
  audioHint, onAudioHintChange,
  costEstimate, estTotalSeconds,
  canRevert, canRestore, restoreLabel, restoreEntries, onRevert, onRestore,
  onGenerate, onClose, generateDisabled,
  selectedShotIds, setSelectedShotIds, toggleShotSelection, toggleSceneSelection, onSetInitialPair,
  autoScopeSwitch, setAutoScopeSwitch,
  hasScreenplay, sceneActions,
}: Props) {
  const { t } = useTranslation('aiWorkspace');
  const isMobile = useMobileLayout();
  const [restoreMenuOpen, setRestoreMenuOpen] = useState(false);

  // Dropdown state
  const [taskDropdownOpen, setTaskDropdownOpen] = useState(false);
  const toggleTaskDropdown = useCallback(() => setTaskDropdownOpen(v => !v), []);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const toggleModelDropdown = useCallback(() => setModelDropdownOpen(v => !v), []);
  const [scopeDropdownOpen, setScopeDropdownOpen] = useState(false);
  const toggleScopeDropdown = useCallback(() => setScopeDropdownOpen(v => !v), []);

  // Save/restore skipApproved and overwriteExisting when entering/leaving selectedShots scope
  const savedSkipApproved = useRef(skipApproved);
  const savedOverwrite = useRef(overwriteExisting);
  const prevScope = useRef(scope);
  useEffect(() => {
    if (scope === 'selectedShots' && prevScope.current !== 'selectedShots') {
      savedSkipApproved.current = skipApproved;
      savedOverwrite.current = overwriteExisting;
      onToggleSkipApproved(false);
      onToggleOverwrite(false);
    } else if (scope !== 'selectedShots' && prevScope.current === 'selectedShots') {
      onToggleSkipApproved(savedSkipApproved.current);
      onToggleOverwrite(savedOverwrite.current);
    }
    prevScope.current = scope;
  }, [scope, skipApproved, onToggleSkipApproved, overwriteExisting, onToggleOverwrite]);

  // Auto-switch from Selected Shots → All Shots when selection becomes empty,
  // but only if the user arrived here via a grid tap (autoScopeSwitch=true).
  // When they manually picked "Selected Shots" from the dropdown, let them stay.
  useEffect(() => {
    if (task === 'continuous') return;
    if (autoScopeSwitch && scope === 'selectedShots' && selectedShotIds.size === 0) {
      onScopeChange('all');
    }
  }, [scope, selectedShotIds, onScopeChange, task, autoScopeSwitch]);

  // Prompt popover state
  const [promptPopoverOpen, setPromptPopoverOpen] = useState(false);
  const promptPillRef = useRef<HTMLButtonElement>(null);
  const handleGridScroll = useCallback(() => {
    setPromptPopoverOpen(false);
  }, []);
  const mainRef = useRef<HTMLDivElement>(null);

  // Fullscreen preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewShotId, setPreviewShotId] = useState<number | null>(null);
  const previewSourceEl = useRef<HTMLElement | null>(null);

  // Project data for the grid
  const project = useProjectStore(s => s.project);
  const videoShots = useProjectStore(s => s.videoShots);
  const allScenes = project?.scenes ?? [];
  const separator = project?.documentSettings?.separator ?? '';
  const allShots = useMemo(
    () => allScenes.flatMap(s => s.shots),
    [allScenes],
  );
  const videoShotIds = useMemo(
    () => new Set(videoShots.map(v => v.shotId)),
    [videoShots],
  );
  const approvals = useCommentStore(s => s.approvals);
  const approvedShotIds = useMemo(
    () => new Set(approvals.filter(a => a.status === 'approved').map(a => a.shotId)),
    [approvals],
  );

  // Track selection order for the preview thumbnail
  const [selectionOrder, setSelectionOrder] = useState<number[]>([]);
  const prevSelectedIds = useRef(selectedShotIds);
  useEffect(() => {
    setSelectionOrder(prev => {
      let next = [...prev];
      // Append newly added IDs
      for (const id of selectedShotIds) {
        if (!prevSelectedIds.current.has(id)) {
          next.push(id);
        }
      }
      // Remove IDs no longer selected
      next = next.filter(id => selectedShotIds.has(id));
      return next;
    });
    prevSelectedIds.current = selectedShotIds;
  }, [selectedShotIds]);

  const previewShot = useMemo(() => {
    if (selectedShotIds.size === 0) return null;
    const targetId = selectionOrder.length > 0 ? selectionOrder[selectionOrder.length - 1] : [...selectedShotIds][0];
    return allShots.find(s => s.id === targetId) ?? null;
  }, [selectedShotIds, selectionOrder, allShots]);

  // Account state for auth / balance checks
  const accountUser = useAccountStore(s => s.accountUser);
  const accountBalance = useAccountStore(s => s.accountBalance);
  const setAuthDialogOpen = useAccountStore(s => s.setAuthDialogOpen);
  const standaloneAuthMode = useAccountStore(s => s.standaloneAuthMode);

  const insufficient = accountBalance && !accountBalance.isBeta &&
    !canAfford(accountBalance, costEstimate.totalTokens);
  const needsAuth = !accountUser;

  // Tutorial step 4: the glow used to be wired to WorkspaceMain's options branch, which
  // AiWorkspace renders only when step !== 'options' — i.e. never for this screen — so the
  // "Enhance Shot" beat has silently shown NO glow since the StyleGradeTab extraction. This
  // is the live button; the same wiring also revives the legacy GlowUp tour's second beat.
  //
  // The example tour's glow is DERIVED from live tutorial state (not the one-shot
  // glowTutorialStep hand-off flag): derived state survives a Discard→options round trip
  // (the rail still says "Tap Enhance on the glowing shot" — it must still glow) and can't
  // go stale after the tour ends. The flag remains only for the legacy GlowUp tour.
  const glowTutorialStep = useUiStore(s => s.glowTutorialStep);
  const setGlowTutorialStep = useUiStore(s => s.setGlowTutorialStep);
  // 20260921 — EXAMPLE-ONLY (design §2.1): the glow AND the paywall bypass below both belong to the
  // Getting started render beat's free baked sketch. A characters run generates for real and pays.
  const tutorialRenderActive = useTutorialStore(s => selectExampleTourActive(s) && s.step === 'render');
  const isTutorialGlow = (glowTutorialStep === 'enhance' || tutorialRenderActive) && isEnhance;
  // The FREE baked render is exactly useAiWorkspaceFlow's routing condition (single-shot
  // enhance during the render beat). The paywall bypass below must be co-extensive with it:
  // a broader bypass would let a real, credit-spending generation (task switched to Video,
  // or scope widened to All) skip the insufficient-balance pre-flight mid-tour.
  const tutorialFreeRender = tutorialRenderActive && isEnhance && scopeShots.length === 1;

  // Model list + tier mapping
  const category: ModelCategory = isEnhance ? 'image' : 'video';
  const models = catalog
    .filter(m => m.category === category && m.id !== 'flux2-flash')
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const maxSeconds = Math.max(...models.map(m => m.estSeconds), 1);
  const selectedModel = models.find(m => m.id === activeModelId);
  const selectedIdx = models.findIndex(m => m.id === activeModelId);
  // Video models carry their own names (MiniMax H3 Max / 2K) — tier words are image-only.
  const selectedTier = category === 'video'
    ? (selectedModel?.name ?? 'Select')
    : selectedIdx >= 0 && selectedIdx < TIER_LABEL_KEYS.length
      ? t(TIER_LABEL_KEYS[selectedIdx])
      : selectedModel?.name ?? 'Select';
  /** "10cr/s" for per-second video models, "50cr" for flat models */
  const priceLabel = (m: CatalogModel | undefined) =>
    m == null ? '' : m.costPerSecond != null ? `${m.costPerSecond}${t('creditSuffix')}/s` : `${m.cost} ${t('creditSuffix')}`;

  // V2 scope dropdown — three fixed options
  const V2_SCOPE_OPTIONS = [
    { value: 'selectedShots' as const, label: t('scopeSelectedShots') },
    { value: 'selectedScenes' as const, label: t('scopeSelectedScenes') },
    { value: 'all' as const, label: t('scopeAllShots') },
  ];

  const getScopeLabel = () => {
    const opt = V2_SCOPE_OPTIONS.find(o => o.value === scope);
    return opt?.label ?? t('scopeAllShots');
  };

  // Generate button label — continuous mode is always 1 video (a pair)
  const unitCount = task === 'continuous' && scopeShots.length >= 2 ? 1 : scopeShots.length;
  const needsSelection = (scope === 'selectedScenes' || scope === 'selectedShots') && selectedShotIds.size === 0;
  const buttonLabel = isEnhance
    ? (unitCount > 1 ? t('enhanceShotsPlural', { count: unitCount }) : t('enhanceShotSingular'))
    : (unitCount > 1 ? t('generateVideosPlural', { count: unitCount }) : t('generateVideoSingular'));

  // Grid tap handler — depends on scope and task
  const handleGridTap = useCallback((shotId: number) => {
    if (task === 'continuous') {
      // Continuous mode: select tapped shot + next sequential shot as a pair
      const idx = allShots.findIndex(s => s.id === shotId);
      if (idx === -1) return;
      const nextShot = allShots[idx + 1];

      if (selectedShotIds.has(shotId)) {
        // Already selected — if tapping the "from" shot, deselect both.
        // If tapping the "to" shot, promote it to the new "from" and auto-select the next.
        const currentFromId = scopeShots.length >= 1 ? scopeShots[0].id : undefined;
        if (shotId === currentFromId || !nextShot) {
          // Tapped the first shot or last shot — clear the pair
          setSelectedShotIds(new Set<number>());
          onSetInitialPair(null);
        } else {
          // Tapped the second shot — make it the new first, select its next
          setSelectedShotIds(new Set([shotId, nextShot.id]));
          onSetInitialPair({ fromShotId: shotId, toShotId: nextShot.id });
        }
      } else if (nextShot) {
        // Select this shot + next shot as a pair
        const next = new Set([shotId, nextShot.id]);
        setSelectedShotIds(next);
        onSetInitialPair({ fromShotId: shotId, toShotId: nextShot.id });
      }
      // If no next shot (last shot), do nothing — can't form a pair
      return;
    }
    if (scope === 'all') {
      // Switch to Selected Shots and select the tapped shot.
      // Enable auto-switch so deselecting all shots returns to "all".
      // Must come after onScopeChange which sets autoScopeSwitch=false.
      onScopeChange('selectedShots');
      setAutoScopeSwitch(true);
      toggleShotSelection(shotId);
      return;
    }
    if (scope === 'selectedScenes') {
      toggleSceneSelection(shotId);
    } else if (scope === 'selectedShots') {
      toggleShotSelection(shotId);
    }
  }, [scope, task, allShots, selectedShotIds, scopeShots, onScopeChange, toggleSceneSelection, toggleShotSelection, setSelectedShotIds, onSetInitialPair]);

  // Grid preview handler — long-press / double-click
  const handleGridPreview = useCallback((shotId: number, cellEl: HTMLElement) => {
    previewSourceEl.current = cellEl;
    setPreviewShotId(shotId);
    setPreviewOpen(true);
  }, []);

  // ImageFlyOverlay items — all shots with thumbnails
  const previewItems = useMemo(
    () => allShots.filter(s => s.thumbnailDataUrl).map(s => ({ url: s.thumbnailDataUrl! })),
    [allShots],
  );
  const shotsWithThumbs = useMemo(
    () => allShots.filter(s => s.thumbnailDataUrl),
    [allShots],
  );
  const previewActiveIndex = useMemo(() => {
    if (previewShotId == null) return 0;
    const idx = shotsWithThumbs.findIndex(s => s.id === previewShotId);
    return idx >= 0 ? idx : 0;
  }, [previewShotId, shotsWithThumbs]);

  const handlePreviewIndexChange = useCallback((newIndex: number) => {
    const shot = shotsWithThumbs[newIndex];
    if (shot) setPreviewShotId(shot.id);
  }, [shotsWithThumbs]);

  // Metadata Add menu (actors / scene)
  const actorSummary = useMemo(() => {
    const seen = new Set<string>();
    let men = 0;
    let women = 0;
    let other = 0;
    for (const shot of scopeShots) {
      if (shot.cast) shot.cast.forEach(c => {
        if (seen.has(c.name)) return;
        seen.add(c.name);
        const g = c.gender?.toLowerCase();
        if (g === 'male') men++;
        else if (g === 'female') women++;
        else other++;
      });
    }
    if (men + women + other === 0) return '';
    const parts: string[] = [];
    if (men > 0) parts.push(`${men} ${men === 1 ? 'man' : 'men'}`);
    if (women > 0) parts.push(`${women} ${women === 1 ? 'woman' : 'women'}`);
    if (other > 0) parts.push(`${other} ${other === 1 ? 'person' : 'people'}`);
    return `${parts.join(', ')} in shot`;
  }, [scopeShots]);

  const sceneDescriptionText = useMemo(() => {
    if (sceneActions.length === 0) return '';
    return sceneActions
      .sort((a, b) => a.scriptOrder - b.scriptOrder)
      .slice(0, 3)
      .map(a => a.actionText)
      .join(' ');
  }, [sceneActions]);



  return (
    <div ref={mainRef} className={styles.main} style={{ gap: 8 }}>
      {/* Controls + Preview row */}
      <div className={styles.controlsRow}>
      {/* Left column: pills + action bar */}
      <div className={styles.controlsLeft}>
      {/* ── Pill row: Task → Model → Scope ──
          Each label lives INSIDE its own .pillWrapper so the pills stay label+picker stacks; .pillRow
          is align-items: flex-end so the triggers keep a common baseline even when a pill (Range)
          drops out. Same .sectionLabel class Create and Touch Up use — one visual family. */}
      <div className={styles.pillRow}>
        <div className={styles.pillWrapper}>
          <div className={styles.sectionLabel}>{t('sectionLabelType')}</div>
          <DropdownPicker
            selectedLabel={t(TASK_LABEL_KEYS[task])}
            open={taskDropdownOpen}
            onToggle={toggleTaskDropdown}
          >
            <div className={styles.pickerList}>
              {(['enhance', 'video', 'continuous'] as const).map(tk => (
                <button
                  key={tk}
                  type="button"
                  className={`${styles.pickerItem} ${tk === task ? styles.pickerItemSelected : ''} ${tk === 'continuous' && !continuousAvailable ? styles.pickerItemDisabled : ''}`}
                  disabled={tk === 'continuous' && !continuousAvailable}
                  onClick={() => {
                    onTaskChange(tk);
                    setTaskDropdownOpen(false);
                  }}
                >
                  {t(TASK_LABEL_KEYS[tk])}
                  {tk === 'continuous' && !continuousAvailable && (
                    <span className={styles.pickerItemHint}>{t('needsNextShot')}</span>
                  )}
                </button>
              ))}
            </div>
          </DropdownPicker>
        </div>

        <div className={styles.pillWrapper}>
          <div className={styles.sectionLabel}>{t('sectionLabelModel')}</div>
          <DropdownPicker
            selectedLabel={`${selectedTier} ${priceLabel(selectedModel)}`}
            open={modelDropdownOpen}
            onToggle={toggleModelDropdown}
          >
            <div className={styles.tierCards}>
              {models.map((m, i) => {
                const selected = m.id === activeModelId;
                const tierLabel = category === 'video' ? m.name : i < TIER_LABEL_KEYS.length ? t(TIER_LABEL_KEYS[i]) : m.name;
                const speedPct = Math.round(((maxSeconds - m.estSeconds) / maxSeconds) * 100 + 20);

                return (
                  <button
                    key={m.id}
                    className={`${styles.tierCard} ${selected ? styles.tierCardSelected : ''}`}
                    onClick={() => {
                      onSelectModel(category, m.id);
                      setModelDropdownOpen(false);
                    }}
                  >
                    <div className={styles.tierHeader}>
                      <span className={styles.tierLabel}>{tierLabel}</span>
                      <span className={styles.tierCost}>{priceLabel(m)}</span>
                    </div>
                    <div className={styles.tierSpeed}>
                      <div className={styles.tierSpeedFill} style={{ width: `${Math.min(100, speedPct)}%` }} />
                    </div>
                    <div className={styles.tierMeta}>
                      <span>{m.name}</span>
                      <span>~{m.estSeconds}s</span>
                      {m.resolution && <span className={styles.tierBadge}>{m.resolution}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => {
                setModelDropdownOpen(false);
                onOpenHistory();
              }}
              style={{
                display: 'block', width: '100%', padding: '8px 10px',
                background: 'transparent',
                border: 'none', borderRadius: 4,
                borderTop: '1px solid var(--chrome-overlay-weak)',
                color: 'var(--text-secondary)',
                fontSize: 12, textAlign: 'left', cursor: 'pointer',
                marginTop: 2,
              }}
            >
              {isEnhance ? t('useSavedImage') : t('useSavedVideo')}
            </button>
          </DropdownPicker>
        </div>

        {/* Label rides inside the conditional: First/Last Shot Video fixes its own pair of shots, so
            there is no range to choose and an orphaned heading would be worse than none. */}
        {task !== 'continuous' && (
          <div className={styles.pillWrapper}>
            <div className={styles.sectionLabel}>{t('sectionLabelRange')}</div>
            <DropdownPicker
              selectedLabel={getScopeLabel()}
              open={scopeDropdownOpen}
              onToggle={toggleScopeDropdown}
            >
              <div className={styles.pickerList}>
                {V2_SCOPE_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`${styles.pickerItem} ${opt.value === scope ? styles.pickerItemSelected : ''}`}
                    onClick={() => {
                      onScopeChange(opt.value);
                      setScopeDropdownOpen(false);
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </DropdownPicker>
          </div>
        )}
      </div>

      {/* ── Action bar: checkboxes stacked left, generate right ── */}
      <div className={styles.actionBar}>
        {/* Left side — hidden for selectedShots (checkboxes don't apply) */}
        {(task === 'continuous' || scope !== 'selectedShots' || canRevert || canRestore) && (
        <div className={styles.actionBarLeft}>
          {task !== 'continuous' && scope !== 'selectedShots' && (
            <label className={styles.checkboxRow} style={{ whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={skipApproved} onChange={e => onToggleSkipApproved(e.target.checked)} />
              {t('skipApprovedShots')}
            </label>
          )}
          {task !== 'continuous' && scope !== 'selectedShots' && showOverwriteOption && (
            <label className={styles.checkboxRow} style={{ whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={overwriteExisting} onChange={e => onToggleOverwrite(e.target.checked)} />
              {t('overwriteExistingStatic')}
            </label>
          )}
          {task === 'continuous' && scopeShots.length >= 2 && (
            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
              {t('continuousEndFrameHint', { shot: scopeShots[1].number ?? '?' })}
            </div>
          )}
        </div>
        )}

      </div>

      {/* ── Row 2: style presets + prompt pill ── */}
      <div className={styles.bottomRow}>
        {isEnhance ? (
          /* The label needs a column to sit in, so the pair gets a wrapper. .styleGroup carries
             flex: 2 to keep the ~2/3 of the row the two buttons held via
             `.bottomRow .stylePresetBtn { flex: 1 }` — that descendant rule still applies, but its
             flex parent is now .styleGroupRow, so it splits the group instead of the row. The button
             class itself is untouched: BoardEnhanceDialog shares it. */
          <div className={styles.styleGroup}>
            <div className={styles.sectionLabel}>{t('sectionLabelStyle')}</div>
            <div className={styles.styleGroupRow}>
              {(['photo', 'sketch'] as const).map(p => (
                <button
                  key={p}
                  className={`${styles.stylePresetBtn} ${stylePreset === p ? styles.stylePresetBtnActive : ''}`}
                  onClick={() => onStylePresetChange(p)}
                >{p.charAt(0).toUpperCase() + p.slice(1)}</button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <label className={styles.checkboxRow} style={{ whiteSpace: 'nowrap', flex: 1 }}>
              <input type="checkbox" checked={generateAudio} onChange={e => onToggleAudio(e.target.checked)} />
              {t('generateAiAudio')}
            </label>
            <label className={styles.checkboxRow} style={{ whiteSpace: 'nowrap', flex: 1 }}>
              <input type="checkbox" checked={retimeToMatchVideo} onChange={onToggleRetime} />
              {t('retimeShotsToMatchVideo')}
            </label>
          </>
        )}
        <button
          ref={promptPillRef}
          type="button"
          className={styles.editPromptPill}
          onClick={() => setPromptPopoverOpen(v => !v)}
        >
          {t('promptPill', 'Prompt')} &#9998;
        </button>
      </div>

      {/* Audio direction hint — shown when Generate Audio is checked in video mode */}
      {!isEnhance && generateAudio && (
        <input
          type="text"
          className={styles.audioHintInput}
          placeholder={t('audioDirectionPlaceholder')}
          value={audioHint}
          onChange={e => onAudioHintChange(e.target.value)}
        />
      )}

      {/* ── Row 3: generate button centered ── */}
      <div className={styles.generateRow}>
        {needsAuth && (!capabilities.bridge || standaloneAuthMode) ? (
          <button className={styles.authBtn} onClick={() => { onClose(); setAuthDialogOpen(true); }}>
            <SparkleIcon />
            {isEnhance ? t('signInToEnhance') : t('signInToGenerate')}
          </button>
        ) : needsSelection ? (
          <button className={styles.generateBtn} disabled>
            <SparkleIcon />
            {t('selectShotsToEnhance')}
          </button>
        ) : (
          <button
            className={`${styles.generateBtn} ${isTutorialGlow ? styles.tutorialGlow : ''}`}
            data-glow-target="enhance"
            onClick={async () => {
              // Tutorial bypass: ONLY the free baked render skips the purchase pre-flight
              // (see tutorialFreeRender above — anything broader spends real credits), so it
              // narrows the gate's `insufficient` rather than skipping the gate.
              const gated = await runGenerateGate({
                needsAuth,
                insufficient: !!insufficient && !tutorialFreeRender,
                onCloseHost: onClose,
              });
              if (gated) return;
              // Consume the legacy hand-off flag only — the example tour's glow is derived
              // and re-arms itself if the user Discards and comes back.
              if (glowTutorialStep === 'enhance') setGlowTutorialStep(null);
              onGenerate();
            }}
            disabled={generateDisabled}
          >
            <SparkleIcon />
            {buttonLabel}
            <span className={styles.creditBadge}>
              ~{costEstimate.totalTokens}cr
              {estTotalSeconds > 0 && ` · ${formatTimeEstimate(estTotalSeconds)}`}
            </span>
          </button>
        )}
      </div>

      </div>{/* end controlsLeft */}

      {/* Thumbnail — fills right half */}
      <div className={styles.previewFloat}>
        {task === 'continuous' ? (
          scopeShots.length >= 2 ? (
            <WorkspaceShotPreview shot={scopeShots[0]} toShot={scopeShots[1]} isContinuous />
          ) : (
            <div className={styles.emptyState} style={{ fontSize: 11 }}>
              {t('tapShotToSelectPair')}
            </div>
          )
        ) : selectedShotIds.size === 1 && previewShot ? (
          <WorkspaceShotPreview shot={previewShot} />
        ) : unitCount === 0 ? (
          <div className={styles.shotPreview}>
            <div
              className={styles.emptyState}
              style={{ fontSize: 12, fontWeight: 600, padding: '0 16px', textAlign: 'center' }}
            >
              {t('tapShotToEnhance')}
            </div>
          </div>
        ) : (
          <div className={styles.shotPreview}>
            {previewShot?.thumbnailDataUrl ? (
              <>
                <img src={previewShot.thumbnailDataUrl} alt="" />
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'rgba(0, 0, 0, 0.55)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 11, fontWeight: 600,
                }}>
                  {unitCount} {unitCount === 1 ? 'shot' : 'shots'} selected
                </div>
              </>
            ) : (
              <div className={styles.emptyState} style={{ fontSize: 11 }}>
                {unitCount} {unitCount === 1 ? 'shot' : 'shots'} selected
              </div>
            )}
          </div>
        )}
        {(canRevert || canRestore) && (
          <div className={styles.previewActions}>
            {canRevert && (
              <button type="button" className={styles.revertPill} onClick={onRevert}>{t('revert')}</button>
            )}
            {canRestore && restoreEntries.length <= 1 && (
              <button type="button" className={styles.revertPill} onClick={() => onRestore()}>{restoreLabel}</button>
            )}
            {canRestore && restoreEntries.length > 1 && (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <button type="button" className={styles.revertPill} onClick={() => setRestoreMenuOpen(o => !o)}>
                  {restoreLabel} ({restoreEntries.length}) ▾
                </button>
                {restoreMenuOpen && (<>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setRestoreMenuOpen(false)} />
                  <div className={styles.restoreMenu}>
                    {[...restoreEntries].reverse().map((entry, i) => (
                      <button
                        key={entry.id}
                        type="button"
                        className={styles.restoreMenuItem}
                        onClick={() => { onRestore(entry.id); setRestoreMenuOpen(false); }}
                      >
                        {entry.objectUrl && (
                          <img src={entry.objectUrl} className={styles.restoreMenuThumb} alt="" />
                        )}
                        <span className={styles.restoreMenuLabel}>
                          {entry.assetType === 'video' ? t('useSavedVideo') : t('useSavedImage')} #{restoreEntries.length - i}
                          <br />
                          <span style={{ fontSize: 10, opacity: 0.6 }}>{entry.modelId}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </>)}
              </div>
            )}
          </div>
        )}
      </div>
      </div>{/* end controlsRow */}

      {/* ── Divider ── */}
      <div className={styles.gridDivider} />

      {/* ── Shot Grid ─────────────────────────────────────── */}
      <ShotGrid
        shots={allShots}
        scenes={allScenes}
        separator={separator}
        selectedIds={selectedShotIds}
        videoShotIds={videoShotIds}
        approvedShotIds={approvedShotIds}
        onTap={handleGridTap}
        onPreview={handleGridPreview}
        onScroll={handleGridScroll}
        {...(isMobile ? { scrollParent: mainRef.current } : {})}
        initialScrollToId={selectedShotIds.size === 1 ? [...selectedShotIds][0] : null}
      />

      {/* ── Fullscreen Preview Overlay ────────────────────── */}
      <AnimatePresence>
        {previewOpen && previewItems.length > 0 && (
          <ImageFlyOverlay
            key="stylgrade-preview"
            activeIndex={previewActiveIndex}
            items={previewItems}
            sourceElement={previewSourceEl.current}
            onClose={() => setPreviewOpen(false)}
            onIndexChange={handlePreviewIndexChange}
          />
        )}
      </AnimatePresence>

      {/* ── Prompt Popover ──────────────────────────────────── */}
      <PromptPopover
        open={promptPopoverOpen}
        onClose={() => setPromptPopoverOpen(false)}
        anchorEl={promptPillRef.current}
        prompt={editablePrompt}
        onPromptChange={onPromptChange}
        onReset={onPromptReset}
        actorSummary={actorSummary}
        sceneDescription={sceneDescriptionText}
        hasScreenplay={hasScreenplay}
        metadataPreview={editableMetadata}
        showMetadataPreview={scope === 'all'}
        isEnhance={isEnhance}
      />
    </div>
  );
}
